import { useCallback, useState } from "react";

import { useForm, zodResolver } from "@mantine/form";

import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { FetalHeartRateFormFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/components/FetalHeartRateFormFields";
import { FETAL_HEART_RATE_QUESTIONNAIRE_NAME } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/constants/fetal-heart-rate-link-ids";
import type { FetalHeartRateFlowsheetDrawerProps } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/types/fetal-heart-rate-drawer";
import type { FetalHeartRateFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/types/fetal-heart-rate-form";
import {
  getDefaultFetalHeartRateFlowsheetFormValues,
  normalizeFetalHeartRateFlowsheetFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/utils/fetal-heart-rate-form-defaults";
import {
  mapFetalHeartRateFormValuesToQuestionnaireValues,
  mapQuestionnaireResponseToFetalHeartRateFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/utils/fetal-heart-rate-questionnaire-mapping";
import { fetalHeartRateFlowsheetFormSchema } from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/zod-schemas/fetal-heart-rate-flowsheet-form";
import { LaborFlowsheetDrawerShell } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDrawerShell";
import { useLaborFlowsheetDrawerPersist } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/useLaborFlowsheetDrawerPersist";

const FETAL_HEART_RATE_DISPLAY_NAME =
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.FetalHeartRate];

export function mergeFetalHeartRateHydratedEditFormValues({
  mapped,
  hydratedQuestionnaireValues,
}: {
  mapped: FetalHeartRateFlowsheetFormValues;
  hydratedQuestionnaireValues: Record<string, unknown>;
}): FetalHeartRateFlowsheetFormValues {
  const hydratedRoot = hydratedQuestionnaireValues.fetalHeartRate as
    | Record<string, unknown>
    | undefined;
  const hydratedDateTime = hydratedRoot?.fhrDateTime as Record<string, unknown> | undefined;
  const hydratedBeats = hydratedRoot?.fhrBPM as Record<string, unknown> | undefined;
  const hydratedFhrDate = hydratedDateTime?.fhrDate;
  const hydratedFhrTime = hydratedDateTime?.fhrTime;
  const hydratedFhrBPMLow = hydratedBeats?.fhrBPMLow;
  const hydratedFhrBPMHigh = hydratedBeats?.fhrBPMHigh;

  return {
    ...mapped,
    fhrDate: hydratedFhrDate instanceof Date ? hydratedFhrDate : mapped.fhrDate,
    fhrTime: typeof hydratedFhrTime === "string" ? hydratedFhrTime : mapped.fhrTime,
    fhrBPMLow:
      typeof hydratedFhrBPMLow === "string" || typeof hydratedFhrBPMLow === "number"
        ? hydratedFhrBPMLow
        : mapped.fhrBPMLow,
    fhrBPMHigh:
      typeof hydratedFhrBPMHigh === "string" || typeof hydratedFhrBPMHigh === "number"
        ? hydratedFhrBPMHigh
        : mapped.fhrBPMHigh,
  };
}

export function FetalHeartRateFlowsheetDrawer(props: FetalHeartRateFlowsheetDrawerProps) {
  const { opened, onClose, clientId, encounterId, mode } = props;
  const [baselineRefreshKey, setBaselineRefreshKey] = useState(0);

  const bumpBaselineRefresh = useCallback(() => {
    setBaselineRefreshKey((currentKey) => currentKey + 1);
  }, []);

  const form = useForm<FetalHeartRateFlowsheetFormValues>({
    initialValues: getDefaultFetalHeartRateFlowsheetFormValues(),
    validate: zodResolver(fetalHeartRateFlowsheetFormSchema),
  });

  const {
    isLoading,
    isSubmitting,
    loadError,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  } = useLaborFlowsheetDrawerPersist<FetalHeartRateFlowsheetFormValues>({
    opened,
    mode,
    onClose,
    clientId,
    encounterId,
    questionnaireName: FETAL_HEART_RATE_QUESTIONNAIRE_NAME,
    displayName: FETAL_HEART_RATE_DISPLAY_NAME,
    timerKey: "fhr",
    form,
    parseAndNormalizeFormValues: (values) =>
      normalizeFetalHeartRateFlowsheetFormValues(fetalHeartRateFlowsheetFormSchema.parse(values)),
    getDefaultFormValues: getDefaultFetalHeartRateFlowsheetFormValues,
    mapFormValuesToQuestionnaireValues: mapFetalHeartRateFormValuesToQuestionnaireValues,
    mapQuestionnaireResponseToFormValues: mapQuestionnaireResponseToFetalHeartRateFormValues,
    mergeHydratedEditFormValues: mergeFetalHeartRateHydratedEditFormValues,
    questionnaireNotFoundMessage: "Fetal heart rate questionnaire not found.",
    editEntryNotFoundMessage: "Could not load existing fetal heart rate entry.",
    editContext:
      mode === "edit"
        ? {
            observation: props.observation,
            questionnaireDisplayName: props.questionnaireDisplayName,
          }
        : undefined,
    onRefresh: mode === "quick-add" ? props.onRefresh : undefined,
    onAfterPersist: bumpBaselineRefresh,
    title: props.title,
  });

  return (
    <LaborFlowsheetDrawerShell
      opened={opened}
      onClose={onClose}
      error={loadError}
      title={drawerTitle}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      submitButtonText={mode === "edit" ? "Update" : "Add To Flowsheet"}
      formId={`labor-flowsheet-form-${QuestionnaireType.FetalHeartRate}`}
      headerSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    >
      <FetalHeartRateFormFields
        form={form}
        mode={mode}
        clientId={clientId}
        isDrawerOpened={opened}
        encounterId={encounterId}
        baselineRefreshKey={baselineRefreshKey}
      />
    </LaborFlowsheetDrawerShell>
  );
}
